import type { SxProps, Theme } from '@mui/material';

export const styles: Record<string, SxProps<Theme>> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    padding: 0,
    gap: 1,
  },

  // Text Fields Section
  textFieldsContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 1,
    marginBottom: 1,
  },

  textField: {
    width: '100%',
    '& .MuiInputBase-root': {
      fontSize: '0.7rem',
      height: '32px',
    },
    '& .MuiInputBase-input': {
      padding: '6px 8px',
    },
  },

  // Table Container
  tableContainer: {
    width: '100%',
    height: '100%',
    overflow: 'auto',
    borderRadius: '4px',
    tableLayout: 'auto',
    borderCollapse: 'separate',
    borderSpacing: 0,
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      borderRight: 'none',
      padding: '1px 1px',
    },
    '& .MuiTableRow-root': {
      '&:last-child .MuiTableCell-root': {
        borderBottom: 'none',
      },
    },
  },

  // Table Headers
  tableHeader: {
    backgroundColor: theme => theme.palette.baseColors.deepSeaNavy.c100,
    color: theme => theme.palette.baseColors.white.c100,
    fontWeight: 'bold',
    fontFamily: (theme: Theme) => theme.fonts.inter,
    fontSize: '0.8rem',
    textAlign: 'center',
    padding: '2px 4px',
    width: 'auto',
    minWidth: '60px',
  },

  // Metric Cells (Left Column)
  metricCell: {
    backgroundColor: theme => theme.palette.baseColors.white.c100,
    color: theme => theme.palette.baseColors.deepSeaNavy.c100,
    fontWeight: 'normal',
    fontFamily: (theme: Theme) => theme.fonts.inter,
    fontSize: '0.8rem',
    width: '220px',
    minWidth: '220px',
  },

  metricCellBold: {
    backgroundColor: theme => theme.palette.baseColors.gray.c50,
    color: theme => theme.palette.baseColors.deepSeaNavy.c100,
    fontWeight: 'bold',
    fontFamily: (theme: Theme) => theme.fonts.inter,
    fontSize: '0.8rem',
    borderBottom: '1px solid rgb(0, 0, 0) !important',
    borderTop: '1px solid rgb(0, 0, 0) !important',
    width: '220px',
    minWidth: '220px',
  },
  metricCellBoldWhite: {
    backgroundColor: theme => theme.palette.baseColors.white.c100,
    color: theme => theme.palette.baseColors.deepSeaNavy.c100,
    fontWeight: 'bold',
    fontFamily: (theme: Theme) => theme.fonts.inter,
    fontSize: '0.8rem',
    borderBottom: '1px solid rgb(0, 0, 0) !important',
    borderTop: '1px solid rgb(0, 0, 0) !important',
    width: '220px',
    minWidth: '220px',
  },

  metricCellGray: {
    backgroundColor: theme => theme.palette.baseColors.gray.c50,
    color: theme => theme.palette.baseColors.deepSeaNavy.c100,
    fontWeight: 'normal',
    fontFamily: (theme: Theme) => theme.fonts.inter,
    fontSize: '0.8rem',
    width: '220px',
    minWidth: '220px',
  },

  // Data Cells (Year Columns)
  dataCell: {
    backgroundColor: theme => theme.palette.baseColors.white.c100,
    color: theme => theme.palette.baseColors.deepSeaNavy.c100,
    fontFamily: (theme: Theme) => theme.fonts.inter,
    fontSize: '0.8rem',
    textAlign: 'right',
    width: 'auto',
    minWidth: '70px',
  },

  dataCellBold: {
    backgroundColor: theme => theme.palette.baseColors.gray.c50,
    color: theme => theme.palette.baseColors.deepSeaNavy.c100,
    fontFamily: (theme: Theme) => theme.fonts.inter,
    fontSize: '0.8rem',
    fontWeight: 'bold',
    textAlign: 'right',
    width: 'auto',
    minWidth: '70px',
    borderBottom: '1px solid rgb(0, 0, 0) !important',
    borderTop: '1px solid rgb(0, 0, 0) !important',
  },

  dataCellBoldWhite: {
    backgroundColor: theme => theme.palette.baseColors.white.c100,
    color: theme => theme.palette.baseColors.deepSeaNavy.c100,
    fontFamily: (theme: Theme) => theme.fonts.inter,
    fontSize: '0.8rem',
    fontWeight: 'bold',
    textAlign: 'right',
    width: 'auto',
    minWidth: '70px',
    borderBottom: '1px solid rgb(0, 0, 0) !important',
    borderTop: '1px solid rgb(0, 0, 0) !important',
  },

  dataCellGray: {
    backgroundColor: theme => theme.palette.baseColors.gray.c50,
    color: theme => theme.palette.baseColors.deepSeaNavy.c100,
    fontFamily: (theme: Theme) => theme.fonts.inter,
    fontSize: '0.8rem',
    textAlign: 'right',
    width: 'auto',
    minWidth: '70px',
  },

  // Summary rows with borders
  summaryRow: {
    borderTop: '1px solid #e0e0e0',
    borderBottom: '1px solid #e0e0e0',
  },

  // Empty row styling
  emptyRow: {
    height: '2px',
    '& .MuiTableCell-root': {
      border: 'none',
      padding: 0,
      height: '2px',
    },
  },

  // Thumbnail specific styles
  thumbnailContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    backgroundColor: theme => theme.palette.baseColors.gray.c25,
    borderRadius: '4px',
    border: '1px solid #e0e0e0',
  },

  thumbnailIcon: {
    fontSize: '2rem',
    color: theme => theme.palette.baseColors.deepSeaNavy.c100,
  },

  // Placeholder specific styles
  placeholderContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    backgroundColor: theme => theme.palette.baseColors.gray.c25,
    borderRadius: '4px',
    border: '1px dashed #e0e0e0',
  },

  placeholderText: {
    color: theme => theme.palette.baseColors.deepSeaNavy.c100,
    fontFamily: (theme: Theme) => theme.fonts.inter,
    fontSize: '0.8rem',
    fontWeight: 'normal',
  },
};
